// Copyright (c) Docugami, Inc. All rights reserved.

import { sleep } from "./AsyncHelpers";
import { Event, IEvent } from "./Event";

export class OperationCancelledError extends Error {
}

export interface RetryResult<T> {
    result?: T;
    error?: any;
    isComplete: boolean;
}

export interface RetryConfiguration<T> {
    intervalMs: number;
    maxRetryCount: number;
    resultHandler: (result: T) => boolean;
    errorHandler: (error: any) => boolean;
}

export interface IRetryPromise<T> {
    onExecuted: IEvent<RetryResult<T>>;
    execute(): Promise<T>
}

const DefaultConfiguration: RetryConfiguration<any> = {
    intervalMs: 1000,
    maxRetryCount: 5,
    resultHandler: () => true,
    errorHandler: () => true
}

export class RetryPromise<T> implements IRetryPromise<T> {
    private readonly _action: () => Promise<T>;
    private readonly _config: RetryConfiguration<T>;
    private readonly _onExecuted: Event<RetryResult<T>> = new Event();
    private _retryCount: number = 0;
    private _runningPromise: Promise<T> | null = null;
    private _signal?: AbortSignal;

    public get onExecuted(): IEvent<RetryResult<T>> { return this._onExecuted; }

    constructor(action: () => Promise<T>, config: Partial<RetryConfiguration<T>>) {
        this._action = action;
        this._config = {...DefaultConfiguration, ...config};
    }

    execute(signal?: AbortSignal): Promise<T> {
        if (!this._runningPromise) {
            this._signal = signal;
            this._runningPromise = this.executeInternal();
        }

        return this._runningPromise;
    }

    private async executeInternal(): Promise<T> {
        let retryResult: RetryResult<T> = await this.step();

        while (this.canExecute(retryResult)) {
            retryResult.isComplete = false;
            this._onExecuted.raise(retryResult);

            await sleep(this._config.intervalMs);

            retryResult = await this.step();
        }

        retryResult.isComplete = true;
        this._onExecuted.raise(retryResult);

        if (!!retryResult.error) {
            throw retryResult.error;
        }

        return retryResult.result!;
    }

    private async step(): Promise<RetryResult<T>> {
        if (!!this._signal?.aborted) {
            return {
                error: new OperationCancelledError(),
                isComplete: true
            };
        }

        this._retryCount++;

        try {
            const result: T = await this._action();
            return {
                result: result,
                isComplete: false
            }
        }
        catch (error: any) {
            return {
                error: error,
                isComplete: false
            }
        }
    }

    private canExecute(currentResult: RetryResult<T>) {
        if (this._retryCount >= this._config.maxRetryCount) {
            return false;
        }

        if (!!currentResult.error && currentResult.error instanceof OperationCancelledError) {
            return false;
        }

        const canExecute = (currentResult.result !== undefined) ? this._config.resultHandler(currentResult.result) : this._config.errorHandler(currentResult.error!);
        return canExecute;
    }
}

export class CompletedRetryPromise<T> implements IRetryPromise<T> {
    private readonly _result: T;
    private readonly _onExecuted: Event<RetryResult<T>> = new Event();

    public get onExecuted(): IEvent<RetryResult<T>> { return this._onExecuted; }

    constructor(result: T) {
        this._result = result;
    }

    async execute(): Promise<T> {
        this._onExecuted.raise({
            result: this._result,
            isComplete: true
        });

        return this._result;
    }
}
